import { ParamMap } from '@angular/router';
import { CHAT_ACTIONS } from '../../../chat/core/actions';
import type { ActionCard } from '../../../chat/core/hub-types';

/**
 * A chat action (`/task`, `/appointment`, `/note` in a conversation) opens the patient's file with
 * `?action=<page>&text=&assignees=&chat=&due=&date=&time=` (chat/core/actions.ts actionPath), and a chat card's Open with
 * `?action=edit-task&record=<id>`, `?action=edit-appointment&record=<id>` or `?action=edit-note&record=<id>`. Either can
 * name the conversation's practice with `&practice=<groupId>`, which the file switches to first. Once that patient has
 * loaded, the file moves to the page with the rest.
 */
export interface ChatActionRequest {
  /** the patient-outlet path to open */
  path: string[];
  queryParams: { text?: string; assignees?: string; chat?: string; due?: string; date?: string; time?: string };
  /** the clinical groupId of the conversation's practice, where the page must run */
  practice?: string;
}

/** Sent on the file's own URL to drop the request, so it runs once. */
export const CHAT_ACTION_PARAMS_CLEARED = { action: null, text: null, assignees: null, chat: null, due: null, date: null, time: null, record: null, practice: null };

/** The pages a card opens for the record it points at. */
const RECORD_PAGES = ['edit-task', 'edit-appointment', 'edit-note'];

/** The page a chat action asks for, or null. Only pages chat opens, so the URL cannot send the file elsewhere. */
export function chatActionRequest(query: ParamMap): ChatActionRequest | null {
  const page = query.get('action');
  if (!page) return null;
  const practice = query.get('practice');
  const withPractice = (request: ChatActionRequest): ChatActionRequest => (practice ? { ...request, practice } : request);
  if (RECORD_PAGES.includes(page)) {
    const record = query.get('record');
    return record ? withPractice({ path: [page, record], queryParams: {} }) : null;
  }
  if (!CHAT_ACTIONS.some((a) => a.page === page)) return null;
  const queryParams: ChatActionRequest['queryParams'] = {};
  for (const key of ['text', 'assignees', 'chat', 'due', 'date', 'time'] as const) {
    const value = query.get(key);
    if (value) queryParams[key] = value;
  }
  return withPractice({ path: [page], queryParams });
}

const sameId = (a: unknown, b: unknown) => String(a).toLowerCase() === String(b).toLowerCase();

/**
 * Where a chat's request must run: here when it names no practice or the selected one, else the user's practice of
 * that id to switch to. The user's practices are their active memberships only, so any other id is refused.
 */
export function chatActionPractice<G extends { id: unknown }>(
  practice: string | undefined,
  selectedId: unknown,
  groups: readonly G[] | null | undefined,
): 'here' | 'not-member' | { switchTo: G } {
  if (!practice || sameId(practice, selectedId)) return 'here';
  const group = groups?.find((g) => sameId(g.id, practice));
  return group ? { switchTo: group } : 'not-member';
}

/** A YYYY-MM-DD that is a real day, else null. */
function dayOrNull(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value ? value : null;
}

/**
 * What the add-task page is filled in with when a chat opened it: the description, clinical user ids, the
 * conversation, and the due day (YYYY-MM-DD).
 */
export function taskPrefill(query: ParamMap): { text: string; assignees: string[]; chat: string | null; due: string | null } {
  return {
    text: (query.get('text') ?? '').trim(),
    assignees: (query.get('assignees') ?? '').split(',').map((id) => id.trim()).filter(Boolean),
    chat: query.get('chat') || null,
    due: dayOrNull(query.get('due')),
  };
}

/** The ids the "Assigned to" select offers, as it spells them, once each; anyone else is dropped. */
export function knownAssignees(ids: readonly string[], members: readonly { userId: string }[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const member = members.find((m) => m.userId.toLowerCase() === id.toLowerCase());
    if (member && !out.includes(member.userId)) out.push(member.userId);
  }
  return out;
}

/**
 * The card a task saved from a chat posts back to it, or null when the save sent back no task id. It carries the
 * assignees by name and id and the due day, so the card shows them from the start.
 */
export function chatTaskCard(
  task: { id?: string; task?: string; dueDate?: string | null; assignees?: { userId?: string; name?: string }[] } | null | undefined,
  patient: { id: string; name?: string; surname?: string },
): ActionCard | null {
  if (!task?.id) return null;
  const card: ActionCard = {
    type: 'task',
    recordId: task.id,
    patientRef: patient.id,
    patientName: [patient.name, patient.surname].filter(Boolean).join(' ') || 'this patient',
    title: task.task ?? '',
  };
  const names = (task.assignees ?? []).map((a) => (a?.name ?? '').trim()).filter(Boolean);
  if (names.length) card.assignees = names;
  // their ids too, so each person's "my tasks" finds it
  const ids = (task.assignees ?? []).map((a) => (a?.userId ?? '').trim()).filter(Boolean);
  if (ids.length) card.assigneeIds = ids;
  // The datepicker saves the chosen day as UTC midnight, so its first ten characters are that day.
  const due = dayOrNull(String(task.dueDate ?? '').slice(0, 10));
  if (due) card.due = due;
  return card;
}

/** An HH:mm that is a time of day, else null. */
function timeOrNull(value: string | null): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value ?? '');
  return m && +m[1]! < 24 && +m[2]! < 60 ? value : null;
}

/** What the booking page is filled in with when a chat opened it: the reason, the conversation, the day and time. */
export function appointmentPrefill(query: ParamMap): { text: string; chat: string | null; date: string | null; time: string | null } {
  return {
    text: (query.get('text') ?? '').trim(),
    chat: query.get('chat') || null,
    date: dayOrNull(query.get('date')),
    time: timeOrNull(query.get('time')),
  };
}

/** "HH:mm" from "9:05" or "09:05", else null. */
function clock(value: unknown): string | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value ?? '').trim());
  return m && +m[1]! < 24 && +m[2]! < 60 ? `${m[1]!.padStart(2, '0')}:${m[2]}` : null;
}

/**
 * The card a booking saved from a chat posts back to it, or null without a booking id or a start. Its title is the
 * type and the reason ("Consultation – follow-up"), as the server writes it when the booking changes.
 */
export function chatAppointmentCard(
  appointment: { id?: string; appointmentType?: string | null; reasonForVisit?: string | null; date?: string | null; timeSlot?: string | null; endSlot?: string | null } | null | undefined,
  patient: { id: string; name?: string; surname?: string },
  doctor: string | null,
): ActionCard | null {
  const day = dayOrNull(String(appointment?.date ?? '').slice(0, 10));
  const time = clock(appointment?.timeSlot);
  if (!appointment?.id || !day || !time) return null;
  const type = (appointment.appointmentType ?? '').trim();
  const reason = (appointment.reasonForVisit ?? '').trim();
  const title = type && reason && type.toLowerCase() !== reason.toLowerCase() ? `${type} – ${reason}` : type || reason;
  const card: ActionCard = {
    type: 'appointment',
    recordId: appointment.id,
    patientRef: patient.id,
    patientName: [patient.name, patient.surname].filter(Boolean).join(' ') || 'this patient',
    title: title.slice(0, 500),
    start: `${day}T${time}`,
    status: 'booked',
  };
  const end = clock(appointment.endSlot);
  if (end) card.end = end;
  if (doctor) card.doctor = doctor;
  return card;
}

const patientName = (patient: { name?: string; surname?: string }) => [patient.name, patient.surname].filter(Boolean).join(' ') || 'this patient';

/** What the note page is filled in with when a chat opened it: the note's text (its lines kept) and the conversation. */
export function notePrefill(query: ParamMap): { text: string; chat: string | null } {
  return { text: (query.get('text') ?? '').trim(), chat: query.get('chat') || null };
}

const NOTE_TITLE_MAX = 120;

/** A note card's title: the note's first line, trimmed, cut to 120 characters with an ellipsis. */
export function noteCardTitle(text: string): string {
  const line = text.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  return line.length > NOTE_TITLE_MAX ? `${line.slice(0, NOTE_TITLE_MAX - 1).trimEnd()}…` : line;
}

/** A note type's template with the chat's text kept above it, so picking a type does not lose what the chat sent. */
export function withChatText(template: string, text: string): string {
  return [text, template].filter(Boolean).join('\n\n');
}

/** The card a note saved from a chat posts back to it, or null without a note id or any text. */
export function chatNoteCard(
  note: { id?: string } | null | undefined,
  patient: { id: string; name?: string; surname?: string },
  body: string,
  noteType: string | null,
): ActionCard | null {
  const title = noteCardTitle(body ?? '');
  if (!note?.id || !title) return null;
  const card: ActionCard = { type: 'note', recordId: note.id, patientRef: patient.id, patientName: patientName(patient), title };
  if (noteType) card.noteType = noteType;
  return card;
}
